import { Component, computed, forwardRef, inject, Injector, model, OnInit, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, NgForm, Validator } from '@angular/forms';
import { Transport, TransportFieldOwner } from 'src/app/partnerportal/models/partner-onboarding';
import { GenericModalService, ModalSettingsTemplate } from 'src/app/shared/generic-modal/generic-modal.service';
import { As2TransportEditorComponent, As2TransportEditorDialogResult, As2TransportResult } from '../as2-transport-editor/as2-transport-editor.component';
import { GroupBoxComponent } from '../../../group-box/group-box.component';
import { OnboardingFile } from 'src/app/partnerportal/components/onboarding-file-selector/models/onboarding-file';

@Component({
  selector: 'app-as2-transport-viewer',
  standalone: true,
  imports: [FormsModule, GroupBoxComponent],
  templateUrl: './as2-transport-viewer.component.html',
  styleUrl: './as2-transport-viewer.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => As2TransportViewerComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => As2TransportViewerComponent),
      multi: true
    }
  ],
})
export class As2TransportViewerComponent implements OnInit, Validator, ControlValueAccessor {
  modalService = inject(GenericModalService);
  private injector = inject(Injector);
  form = viewChild<NgForm>('form');
  value = model<Transport>();
  disabled = signal<boolean>(false);
  ngControl: NgControl | null = null;

  ngOnInit(): void {
    try {
      this.ngControl = this.injector.get(NgControl);
    }
    catch {
      // No NgControl
    }
  }

  get control(): FormControl {
    return this.ngControl?.control as FormControl;
  }

  get isValid(): boolean {
    return !this.control || this.control.invalid !== true;
  }

  customerNote = computed(() => 'Your partner will use the following connection details to connect to your system');
  partnerNote = computed(() => 'Your partner will be asked to provide the following connection details');

  customerHostname = computed(() => {
    const field = this.value()?.TransportFields?.find(f => f.Name === 'HostnameCustomer');
    const hostname = field?.Value;
    return hostname;
  });

  customerAS2Id = computed(() => {
    const field = this.value()?.TransportFields?.find(f => f.Name === 'As2IdCustomer');
    const as2Id = field?.Value;
    return as2Id;
  });

  customerPublicCert = computed(() => {
    const field = this.value()?.TransportFields?.find(f => f.Name === 'PublicCertificateCustomer');
    const cert = JSON.parse(field?.Value ?? null) as OnboardingFile;
    return cert?.FileName;
  });

  partnerFields = computed(() => {
    const fields = this.value()?.TransportFields?.filter(f => f.FilledBy === TransportFieldOwner.Partner) ?? [];
    return fields;
  });

  async editTransport(transport: Transport) {
    const modalSettings = new ModalSettingsTemplate('lg', 'static');
    const result = await this.modalService.openComponentWithInitialize<As2TransportResult, As2TransportEditorComponent>(As2TransportEditorComponent, modalSettings, (dialog) => {
      dialog.transport = transport;
    });

    if (result?.dialogResult === As2TransportEditorDialogResult.Cancel) {
      return;
    }
    this.value.set(result.transport);
    this.onChange(result.transport);
  }

  validateTransport() {
    if (!this.value()?.TransportFields?.find(f => f.Name === 'HostnameCustomer' && f.Value) ||
        !this.value()?.TransportFields?.find(f => f.Name === 'As2IdCustomer' && f.Value) ||
        !this.value()?.TransportFields?.find(f => f.Name === 'PublicCertificateCustomer' && f.Value)) {
      return ['Invalid'];
    }
    return [];
  }


  /*
   * Validator interface methods
   */

  validate() {
    const frm = this.form()?.form;
    if (frm?.disabled ?? true) {
      return [];
    }
    return this.validateTransport();
  }


  /*
   * ControlValueAccessor interface methods
   */

  writeValue(obj: Transport): void {
    this.value.set(obj);
  }

  onChange: (value: unknown) => void = () => { };
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(!!isDisabled);
  }

}
